import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import './NetworkVisualizations.css';

const PROTOCOL_KEYWORDS = [
  'HTTP',
  'FTP',
  'TELNET',
  'SMTP',
  'DNS',
  'TLS',
  'SSL',
  'SMB',
  'RDP',
  'POP3',
  'IMAP',
];

const normalizeLabel = (label) => {
  return (label || 'Unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const inferProtocol = (finding) => {
  const text = [
    finding.type,
    finding.title,
    finding.description,
    finding.code_snippet,
  ].filter(Boolean).join(' ').toUpperCase();

  const protocol = PROTOCOL_KEYWORDS.find((keyword) => text.includes(keyword));
  if (protocol) return protocol;

  if (text.includes('BEACON')) return 'Beaconing';
  if (text.includes('BRUTE')) return 'Brute Force';
  if (text.includes('PORT SCAN')) return 'Port Scan';
  if (text.includes('CREDENTIAL') || text.includes('SECRET')) return 'Credentials';

  return normalizeLabel(finding.type);
};

const buildStats = (findings, summary) => {
  const protocolCounts = {};

  findings.forEach((finding) => {
    const protocol = inferProtocol(finding);
    protocolCounts[protocol] = (protocolCounts[protocol] || 0) + 1;
  });

  if (Object.keys(protocolCounts).length === 0) {
    Object.entries(summary?.type_counts || {}).forEach(([type, count]) => {
      protocolCounts[normalizeLabel(type)] = count;
    });
  }

  return Object.entries(protocolCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);
};

function ProtocolStats({ findings, summary }) {
  const stats = useMemo(() => buildStats(findings, summary), [findings, summary]);

  return (
    <div className="network-visual-card">
      <div className="network-section-header">
        <h2>Protocol Signals</h2>
        <span>{stats.length} categories</span>
      </div>

      {stats.length === 0 ? (
        <p className="network-viz-empty">No protocol signals were detected.</p>
      ) : (
        <div className="protocol-stats-layout">
          <div className="protocol-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats} margin={{ top: 10, right: 10, left: -24, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="name" stroke="#9fa6b2" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} stroke="#9fa6b2" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    background: '#0a0e1e',
                    border: '1px solid rgba(0, 243, 255, 0.25)',
                    color: '#f0f6fc',
                  }}
                />
                <Bar dataKey="count" fill="#00f3ff" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="protocol-breakdown">
            {stats.map((item) => (
              <div key={item.name} className="protocol-row">
                <span>{item.name}</span>
                <label>{item.count} findings</label>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProtocolStats;
